import React, {
    ReactElement, useContext,
} from "react";
import { LogErrorInput, LogLevel } from "../../__generated__/api";
import LogSaver, { createContextData, errorToInput } from "./LogSaver";

export { createContextData } from "./LogSaver";

const LOG_LEVELS = {
    [LogLevel.Trace]: 0,
    [LogLevel.Debug]: 10,
    [LogLevel.Info]: 20,
    [LogLevel.Warning]: 30,
    [LogLevel.Error]: 40,
    [LogLevel.Fatal]: 50,
};
const LOG_LEVEL = (process.env.LOG_LEVEL ?? "DEBUG") as LogLevel;
const PRINT_LEVEL = LOG_LEVELS[LOG_LEVEL];

function parseMessageParams(
    dataOrMessage: Record<string, unknown> | string,
    message?: string,
) {
    let data: Record<string, unknown> | undefined;
    let msg = message;
    if (typeof dataOrMessage === "object") {
        data = dataOrMessage;
    } else if (typeof dataOrMessage !== "string") {
        msg = dataOrMessage;
    }
    return {
        message: msg as string,
        data,
    };
}

function parseParams(
    errorOrData: Error | Record<string, unknown>,
    dataOrMessage: Record<string, unknown> | string,
    message?: string,
) {
    const error = errorOrData instanceof Error ? errorOrData : undefined;
    let data: Record<string, unknown> | undefined;
    if (!(errorOrData instanceof Error)) {
        data = errorOrData;
    } else if (typeof dataOrMessage !== "string") {
        data = dataOrMessage;
    }
    let msg = message;
    if (!msg && typeof dataOrMessage === "string") {
        msg = dataOrMessage;
    }
    return {
        message: msg as string,
        data: {
            error,
            ...data,
        },
    };
}

function createError(data: Record<string, unknown>): LogErrorInput | undefined {
    const error = data.err || data.error;
    return error instanceof Error ? errorToInput(error) : undefined;
}

export class LoggingHandler {
    public static readonly instance = new LoggingHandler(new LogSaver(), { component: "Default" });

    // eslint-disable-next-line no-useless-constructor
    constructor(private saver: LogSaver, private data: Record<string, unknown>) {
        // declare private
    }

    child(data: Record<string, unknown>, component: string): LoggingHandler {
        return new LoggingHandler(this.saver, {
            ...this.data,
            ...data,
            component,
        });
    }

    private addMessage(
        level: LogLevel,
        rawData: Record<string, unknown> | undefined,
        message: string,
        logger: ((...data: unknown[]) => void),
    ) {
        const data = rawData ?? {};
        const timestamp = new Date();
        this.saver.add({
            level,
            message,
            timestamp,
            component: data.component as string ?? "Default",
            data: {
                ...data,
                ...createContextData(),
            },
            error: createError(data),
        });
        if (LOG_LEVELS[level] >= PRINT_LEVEL) {
            logger(timestamp.toLocaleString(), level, message, Object.keys(data).isNotEmpty ? data : "");
        }
    }

    trace(message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    trace(data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    trace(dataOrMessage: Record<string, unknown> | string, message?: string): void {
        const params = parseMessageParams(dataOrMessage, message);
        // eslint-disable-next-line no-console
        this.addMessage(LogLevel.Trace, params.data, params.message, console.trace);
    }

    debug(message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    debug(data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    debug(dataOrMessage: Record<string, unknown> | string, message?: string): void {
        const params = parseMessageParams(dataOrMessage, message);
        // eslint-disable-next-line no-console
        this.addMessage(LogLevel.Debug, params.data, params.message, console.debug);
    }

    info(message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    info(data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    info(dataOrMessage: Record<string, unknown> | string, message?: string): void {
        const params = parseMessageParams(dataOrMessage, message);
        // eslint-disable-next-line no-console
        this.addMessage(LogLevel.Info, params.data, params.message, console.info);
    }

    warn(data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    warn(error: Error, data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    warn(errorOrData: Error | Record<string, unknown>, dataOrMessage: Record<string, unknown> | string, message?: string): void {
        const output = parseParams(errorOrData, dataOrMessage, message);
        // eslint-disable-next-line no-console
        this.addMessage(LogLevel.Warning, output.data, output.message, console.warn);
    }

    error(data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    error(error: Error, data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    error(errorOrData: Error | Record<string, unknown>, dataOrMessage: Record<string, unknown> | string, message?: string): void {
        const output = parseParams(errorOrData, dataOrMessage, message);
        // eslint-disable-next-line no-console
        this.addMessage(LogLevel.Error, output.data, output.message, console.error);
    }

    fatal(data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    fatal(error: Error, data: Record<string, unknown>, message: string): void;

    // eslint-disable-next-line no-dupe-class-members
    fatal(errorOrData: Error | Record<string, unknown>, dataOrMessage: Record<string, unknown> | string, message?: string): void {
        const output = parseParams(errorOrData, dataOrMessage, message);
        // eslint-disable-next-line no-console
        this.addMessage(LogLevel.Fatal, output.data, output.message, console.error);
    }
}

LoggingHandler.instance.info({
    environment: {
        version: window.FOQAL_VERSION_NUMBER,
        hostname: window.location.host,
        path: window.location.pathname,
    },
}, "Starting...");

export const LoggingContext = React.createContext<LoggingHandler>(LoggingHandler.instance);

export function LoggingProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    return (
        <LoggingContext.Provider value={LoggingHandler.instance}>
            {children}
        </LoggingContext.Provider>
    );
}

export default function useLogging(): LoggingHandler {
    return useContext(LoggingContext) ?? LoggingHandler.instance;
}
